import { useCallback, useEffect, useRef, useState } from 'react'

import Picture from '../picture'
import CaretDownFilled from '../svg/CaretFilled'

interface Props {
  description: string
  isExpanded: boolean
  heading: string
  icon?: string
  onToggle: VoidFunction
}

const AccordionItem = ({
  description,
  isExpanded,
  heading,
  icon,
  onToggle,
}: Props) => {
  const [expanded, setExpanded] = useState(isExpanded)
  const ref = useRef<HTMLDivElement>(null)

  const onClick = useCallback(() => {
    setExpanded((prev) => !prev)
    onToggle()
  }, [onToggle])

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = expanded
        ? ref.current.scrollHeight + 'px'
        : '0'
    }
  }, [expanded])

  useEffect(() => {
    setExpanded(isExpanded)
  }, [isExpanded])

  return (
    <section
      key={heading}
      className={`${
        expanded
          ? 'bg-white border border-neutral-20 shadow-md'
          : 'bg-[#f7f7f7] items-center'
      } mt-6 rounded-xl flex px-6 py-4`}
      role="button"
      onClick={onClick}
      aria-expanded={expanded}
    >
      {icon ? (
        <Picture
          image={icon}
          imageAlt={heading}
          aria-hidden="true"
          role="presentation"
          imageStyle="w-12 aspect-square mr-3"
        />
      ) : null}
      <section className="flex flex-col flex-1 h-full">
        <header className="flex justify-between font-natosans-medium text-[#101010] text-lg leading-[1.36]">
          <span>{heading}</span>
          <CaretDownFilled
            className={`${
              expanded ? 'rotate-0' : 'rotate-180'
            } h-4 w-4 transition-transform duration-300 fill-[#101010]`}
          />
        </header>
        <article
          className={`mt-2 font-natosans-light text-neutral-80 leading-[1.56] text-base overflow-hidden transition-all duration-300 ${
            expanded ? 'opacity-100' : 'opacity-0'
          }`}
          ref={ref}
        >
          {description}
        </article>
      </section>
    </section>
  )
}

export default AccordionItem
